// src/services/muteService.ts
import { logger } from '../utils/logger';



import { supabase } from './supabase';



export interface MutedChat {

  id: string;

  user_id: string;

  muted_entity_id: string; // match_id or group_id

  muted_entity_type: 'match' | 'group';

  muted_until: string | null; // null = indefinite

  created_at: string;

}



export type MuteDuration =

  | 'indefinite'

  | '1hour'

  | '8hours'

  | '24hours'

  | '1week';



/**

 * Mute a chat (1-on-1 or group)

 */

export async function muteChat(

  entityId: string,

  entityType: 'match' | 'group',

  duration: MuteDuration = 'indefinite'

): Promise<{ success: boolean; error?: string }> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }



    // Calculate muted_until timestamp

    let mutedUntil: string | null = null;

    if (duration !== 'indefinite') {

      const now = new Date();

      switch (duration) {

        case '1hour':

          now.setHours(now.getHours() + 1);

          break;

        case '8hours':

          now.setHours(now.getHours() + 8);

          break;

        case '24hours':

          now.setHours(now.getHours() + 24);

          break;

        case '1week':

          now.setDate(now.getDate() + 7);

          break;

      }

      mutedUntil = now.toISOString();

    }



    // Upsert (insert or update if exists)

    const { error } = await supabase

      .from('muted_chats')

      .upsert({

        user_id: user.id,

        muted_entity_id: entityId,

        muted_entity_type: entityType,

        muted_until: mutedUntil

      }, {

        onConflict: 'user_id,muted_entity_id,muted_entity_type'

      });



    if (error) {

      logger.error('Error muting chat:', error);

      return { success: false, error: error.message };

    }



    logger.log(`ðŸ”• Muted ${entityType} ${entityId} for ${duration}`);

    return { success: true };

  } catch (error) {

    logger.error('Error in muteChat:', error);

    return { success: false, error: 'Failed to mute chat' };

  }

}



/**

 * Unmute a chat

 */

export async function unmuteChat(

  entityId: string,

  entityType: 'match' | 'group'

): Promise<{ success: boolean; error?: string }> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }



    const { error } = await supabase

      .from('muted_chats')

      .delete()

      .eq('user_id', user.id)

      .eq('muted_entity_id', entityId)

      .eq('muted_entity_type', entityType);



    if (error) {

      logger.error('Error unmuting chat:', error);

      return { success: false, error: error.message };

    }



    logger.log(`ðŸ”” Unmuted ${entityType} ${entityId}`);

    return { success: true };

  } catch (error) {

    logger.error('Error in unmuteChat:', error);

    return { success: false, error: 'Failed to unmute chat' };

  }

}



/**

 * Check if a chat is currently muted

 */

export async function isMuted(

  entityId: string,

  entityType: 'match' | 'group'

): Promise<{ success: boolean; muted: boolean; mutedUntil?: string | null; error?: string }> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, muted: false, error: 'Not authenticated' };

    }



    const { data, error } = await supabase

      .from('muted_chats')

      .select('*')

      .eq('user_id', user.id)

      .eq('muted_entity_id', entityId)

      .eq('muted_entity_type', entityType)

      .maybeSingle();



    if (error) {

      logger.error('Error checking mute status:', error);

      return { success: false, muted: false, error: error.message };

    }



    // If no record, not muted

    if (!data) {

      return { success: true, muted: false };

    }



    // If muted indefinitely

    if (!data.muted_until) {

      return { success: true, muted: true, mutedUntil: null };

    }



    // Check if mute has expired

    const mutedUntil = new Date(data.muted_until);

    const now = new Date();



    if (now > mutedUntil) {

      // Mute has expired, delete it

      await unmuteChat(entityId, entityType);

      return { success: true, muted: false };

    }



    return { success: true, muted: true, mutedUntil: data.muted_until };

  } catch (error) {

    logger.error('Error in isMuted:', error);

    return { success: false, muted: false, error: 'Failed to check mute status' };

  }

}



/**

 * Get all muted chats for current user

 */

export async function getMutedChats(): Promise<{

  success: boolean;

  mutedChats?: MutedChat[];

  error?: string;

}> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }



    const { data, error } = await supabase

      .from('muted_chats')

      .select('*')

      .eq('user_id', user.id)

      .order('created_at', { ascending: false });



    if (error) {

      logger.error('Error fetching muted chats:', error);

      return { success: false, error: error.message };

    }



    // Filter out expired mutes

    const now = new Date();

    const validMutes = (data || []).filter(mute => {

      if (!mute.muted_until) return true; // Indefinite mutes

      return new Date(mute.muted_until) > now;

    });



    // Clean up expired mutes in background

    const expiredMutes = (data || []).filter(mute => {

      if (!mute.muted_until) return false;

      return new Date(mute.muted_until) <= now;

    });



    if (expiredMutes.length > 0) {

      const expiredIds = expiredMutes.map(m => m.id);

      await supabase

        .from('muted_chats')

        .delete()

        .in('id', expiredIds);

    }



    return { success: true, mutedChats: validMutes };

  } catch (error) {

    logger.error('Error in getMutedChats:', error);

    return { success: false, error: 'Failed to fetch muted chats' };

  }

}



/**

 * Format time remaining for muted chat

 */

export function formatMuteDuration(mutedUntil: string | null): string {

  if (!mutedUntil) return 'Muted';



  const until = new Date(mutedUntil);

  const now = new Date();

  const diffMs = until.getTime() - now.getTime();

  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));



  if (diffHours < 1) return 'Muted for <1h';

  if (diffHours === 1) return 'Muted for 1h';

  if (diffHours < 24) return `Muted for ${diffHours}h`;

  if (diffDays === 1) return 'Muted for 1 day';

  return `Muted for ${diffDays} days`;

}